import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import Select from 'react-select';
import adminConsoleService, {
  type SecurityGroupListItem,
  type UserSecurityGroup,
  type AdminUser,
} from '@/services/adminConsoleService';

interface SecurityGroupOption {
  GroupId: number;
  GroupName: string;
}

interface EditSecurityGroupsModalProps {
  open: boolean;
  onClose: () => void;
  personId: number;
  personName: string;
  onSaved?: () => void;
}

const buildTree = (items: SecurityGroupListItem[], search: string) =>
{
  const filtered = search.trim()
    ? items.filter(
      sg =>
        sg.Name.toLowerCase().includes(search.toLowerCase()) ||
          sg.GroupName.toLowerCase().includes(search.toLowerCase()),
    )
    : items;
  const map = new Map<number, { groupName: string; items: SecurityGroupListItem[] }>();
  for (const sg of filtered)
  {
    if (!map.has(sg.GroupId)) map.set(sg.GroupId, { groupName: sg.GroupName, items: [] });
    map.get(sg.GroupId)!.items.push(sg);
  }
  return [...map.entries()].sort((a, b) => a[1].groupName.localeCompare(b[1].groupName));
};

const EditSecurityGroupsModal: React.FC<EditSecurityGroupsModalProps> = ({
  open,
  onClose,
  personId,
  personName,
  onSaved,
}) =>
{
  const [securityGroupList, setSecurityGroupList] = useState<SecurityGroupListItem[]>([]);
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  const [secGroupSearch, setSecGroupSearch] = useState('');
  const [selectedSecGroupIds, setSelectedSecGroupIds] = useState<Set<number>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [savingSecGroups, setSavingSecGroups] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [selectedGroups, setSelectedGroups] = useState<SecurityGroupOption[]>([]);
  const [selectedDefaultGroup, setSelectedDefaultGroup] = useState<SecurityGroupOption | null>(
    null,
  );

  useEffect(() =>
  {
    if (!open || !personId) return;
    setLoading(true);
    setSecGroupSearch('');
    setModalError(null);

    Promise.all([adminConsoleService.getSecurityGroupList(), adminConsoleService.getAdminUsers()])
      .then(([groupList, users]) =>
      {
        const foundUser = users.find(u => u.PersonID === personId) ?? null;
        setSecurityGroupList(groupList);
        setUser(foundUser);

        const currentIds = new Set((foundUser?.SecurityGroups ?? []).map(sg => sg.SecurityGroupID));
        setSelectedSecGroupIds(currentIds);
        const assignedGroupIds = new Set((foundUser?.SecurityGroups ?? []).map(sg => sg.GroupId));
        setExpandedGroups(new Set(assignedGroupIds));

        const defaultGroup = foundUser?.DefaultGroup
          ? (groupList.find(x => x.GroupId === (foundUser.DefaultGroup as any).GroupID) ?? null)
          : null;
        setSelectedDefaultGroup(
          defaultGroup ? { GroupId: defaultGroup.GroupId, GroupName: defaultGroup.GroupName } : null,
        );
      })
      .finally(() => setLoading(false));
  }, [open, personId]);

  // Rebuild the available default group options whenever selection changes
  useEffect(() =>
  {
    const filtered = securityGroupList.filter(x => selectedSecGroupIds.has(x.SecurityGroupID));
    const unique: SecurityGroupOption[] = Object.values(
      Object.fromEntries(
        filtered.map(x => [x.GroupId, { GroupId: x.GroupId, GroupName: x.GroupName }]),
      ),
    );
    setSelectedGroups(unique);

    if (unique.length > 0 && !unique.some(x => x.GroupId === selectedDefaultGroup?.GroupId))
    {
      setSelectedDefaultGroup(unique[0]!);
    }
    else if (unique.length === 0)
    {
      setSelectedDefaultGroup(null);
    }
  }, [selectedSecGroupIds, securityGroupList]);

  const toggleGroupExpand = useCallback((groupId: number) =>
  {
    setExpandedGroups(prev =>
    {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  const getGroupCheckState = useCallback(
    (groupId: number): 'all' | 'some' | 'none' =>
    {
      const children = securityGroupList.filter(sg => sg.GroupId === groupId);
      const selected = children.filter(sg => selectedSecGroupIds.has(sg.SecurityGroupID));
      if (selected.length === 0) return 'none';
      if (selected.length === children.length) return 'all';
      return 'some';
    },
    [securityGroupList, selectedSecGroupIds],
  );

  const toggleGroupAll = useCallback(
    (groupId: number) =>
    {
      const children = securityGroupList.filter(sg => sg.GroupId === groupId);
      const state = getGroupCheckState(groupId);
      setSelectedSecGroupIds(prev =>
      {
        const next = new Set(prev);
        if (state === 'all')
        {
          children.forEach(sg => next.delete(sg.SecurityGroupID));
        }
        else
        {
          children.forEach(sg => next.add(sg.SecurityGroupID));
        }
        return next;
      });
    },
    [securityGroupList, getGroupCheckState],
  );

  const toggleSecGroup = useCallback((id: number) =>
  {
    setSelectedSecGroupIds(prev =>
    {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const editDefaultSecurityGroup = useCallback((group: SecurityGroupOption | null) =>
  {
    setSelectedDefaultGroup(group);
  }, []);

  const saveSecGroups = async () =>
  {
    if (!user) return;
    setSavingSecGroups(true);
    setModalError(null);
    try
    {
      const newGroupsRequest: UserSecurityGroup[] = securityGroupList
        .filter(sg => selectedSecGroupIds.has(sg.SecurityGroupID))
        .map(sg => ({
          Name: sg.Name,
          PersonID: user.PersonID,
          SecurityGroupID: sg.SecurityGroupID,
          GroupId: sg.GroupId,
          IsAdminUserGroup: false,
          Status: null,
          Checked: true,
          IsInternal: false,
        }));

      const payload: AdminUser = {
        ...user,
        SecurityGroups: newGroupsRequest,
        SecurityGroupID: selectedDefaultGroup?.GroupId ?? user.SecurityGroupID,
        GroupIds: '',
        DefaultGroup: selectedDefaultGroup
          ? {
            GroupID: selectedDefaultGroup.GroupId,
            GroupName: selectedDefaultGroup.GroupName,
            PersonID: user.PersonID,
          }
          : (user as any).DefaultGroup,
      };
      delete (payload as any).Status;
      delete (payload as any).PTOApprover;
      delete (payload as any).HRApprover;
      delete (payload as any).SubmitTimeTeam;

      await adminConsoleService.updatePersonSecurityGroups(payload);
      onSaved?.();
      onClose();
    }
    catch
    {
      setModalError('Failed to save security groups.');
    }
    finally
    {
      setSavingSecGroups(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="admin-console-portal fixed inset-0 z-[10003] flex items-center justify-center">
      <div className="fixed inset-0 bg-black/30" onClick={onClose} />
      <div className="relative z-10 bg-white dark:bg-[#2d2f2f] rounded shadow-xl w-[460px] max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3">
          <div>
            <h3 className="text-[16px] font-medium text-[#62aacb]">Edit User's Security Groups</h3>
            <p className="text-[20px] text-[#414042] dark:text-[#a9a093] mt-0.5">{personName}</p>
          </div>
          <button onClick={onClose} className="text-[#bbb] hover:text-[#666]">
            <span className="material-icons text-[18px]">close</span>
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-10 text-[13px] text-[#888]">
            Loading...
          </div>
        ) : (
          <>
            {/* Default Group */}
            <div className="flex items-center gap-3 px-5 pb-3">
              <span className="text-[12px] text-[#555] flex-shrink-0">User's Default Group</span>
              <Select<SecurityGroupOption>
                value={selectedDefaultGroup}
                options={selectedGroups}
                onChange={editDefaultSecurityGroup}
                getOptionLabel={x => x.GroupName}
                getOptionValue={x => String(x.GroupId)}
                placeholder="Select a Default Group..."
                className="flex-1 text-[12px]"
                classNamePrefix="esgp-select"
                styles={{
                  control: base => ({
                    ...base,
                    minHeight: '32px',
                    fontSize: '12px',
                    borderColor: '#ccc',
                    borderRadius: '4px',
                    margin: 0,
                  }),
                  indicatorSeparator: () => ({ display: 'none' }),
                }}
              />
            </div>

            {/* Filter */}
            <div className="flex items-center gap-3 px-5 pb-3">
              <span className="text-[12px] text-[#555] flex-shrink-0">Filter:</span>
              <input
                autoFocus
                type="text"
                value={secGroupSearch}
                onChange={e => setSecGroupSearch(e.target.value)}
                className="flex-1 border border-[#ccc] rounded px-2 py-1 text-[12px] outline-none focus:border-[#62aacb]"
              />
            </div>

            {/* Warning */}
            <p className="px-5 text-[12px] text-[#414042] dark:text-[#a9a093] mb-2">
              If you check a security group, the user will be assigned as an administrator of all
              security sub-groups. If you uncheck a sub security group, administrator permissions
              will be removed for such security group.
            </p>

            {/* Tree */}
            <div className="flex-1 overflow-hidden mx-5 mb-3 border border-[#ddd] rounded flex flex-col">
              <div className="px-3 py-1.5 bg-[#f5f5f5] border-b border-[#ddd] text-[11px] font-semibold text-[#555]">
                Name
              </div>
              <div className="flex-1 overflow-y-auto">
                {buildTree(securityGroupList, secGroupSearch).map(
                  ([groupId, { groupName, items }]) =>
                  {
                    const isExpanded = secGroupSearch.trim() ? true : expandedGroups.has(groupId);
                    const checkState = getGroupCheckState(groupId);
                    return (
                      <div key={groupId}>
                        <div className="flex items-center gap-2 px-3 py-2 bg-[#f9f9f9] border-b border-[#eee] hover:bg-[#f0f0f0]">
                          <IndeterminateCheckbox
                            id={`esgp-group-${groupId}`}
                            checked={checkState === 'all'}
                            indeterminate={checkState === 'some'}
                            onChange={() => toggleGroupAll(groupId)}
                          />
                          <button
                            onClick={() => toggleGroupExpand(groupId)}
                            className="flex items-center gap-1 flex-1 text-left"
                          >
                            <span className="material-icons text-[14px] text-[#888]">
                              {isExpanded ? 'expand_more' : 'chevron_right'}
                            </span>
                            <span className="text-[12px] font-semibold text-[#444]">
                              {groupName}
                            </span>
                            <span className="text-[11px] text-[#aaa] ml-1">({items.length})</span>
                          </button>
                        </div>
                        {isExpanded &&
                          items.map(sg => (
                            <div
                              key={sg.SecurityGroupID}
                              className="flex items-center gap-2 pl-9 pr-3 py-2 border-b border-[#f5f5f5] hover:bg-[#f9f9f9]"
                            >
                              <span className="corgee_com_checkbox flex-shrink-0">
                                <input
                                  id={`esgp-sg-${sg.SecurityGroupID}`}
                                  type="checkbox"
                                  checked={selectedSecGroupIds.has(sg.SecurityGroupID)}
                                  onChange={() => toggleSecGroup(sg.SecurityGroupID)}
                                />
                                <label htmlFor={`esgp-sg-${sg.SecurityGroupID}`}></label>
                              </span>
                              <label
                                htmlFor={`esgp-sg-${sg.SecurityGroupID}`}
                                className="text-[12px] text-[#333] cursor-pointer"
                              >
                                {sg.Name}
                              </label>
                            </div>
                          ))}
                      </div>
                    );
                  },
                )}
              </div>
            </div>

            {modalError && <p className="text-[11px] text-[#e57373] px-5 pb-2">{modalError}</p>}

            {/* Footer */}
            <div className="px-5 py-3 border-t border-[#eee] flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-4 py-1.5 text-[12px] bg-[#999] dark:bg-[#555] text-white rounded hover:bg-[#777] dark:hover:bg-[#666]"
              >
                Cancel
              </button>
              <button
                onClick={saveSecGroups}
                disabled={savingSecGroups}
                className="px-4 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] disabled:opacity-50"
              >
                {savingSecGroups ? 'Saving...' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

// Helper: checkbox with indeterminate support
const IndeterminateCheckbox: React.FC<{
  id: string;
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
}> = ({ id, checked, indeterminate, onChange }) =>
{
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() =>
  {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <span className="corgee_com_checkbox flex-shrink-0">
      <input ref={ref} id={id} type="checkbox" checked={checked} onChange={onChange} />
      <label htmlFor={id}></label>
    </span>
  );
};

export default EditSecurityGroupsModal;
